import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AIIllustrationModel } from 'models/AIIllustrationModel';
import { IAIIllustrationCreate } from 'types/aiIllustration';
import AIIllustrationVocCategorySelectModel, {
  IVocCategoryWithCount,
} from 'models/AIIllustrationVocCategorySelectModel';

interface IProps {
  model: AIIllustrationModel;
  onClose: () => void;
  onSuccess: (jobId: number) => void;
}

const AIIllustrationVocabularyCreate: React.FC<IProps> = ({ model, onClose, onSuccess }) => {
  const [name, setName] = useState<string>('');
  const [vocCategoryId, setVocCategoryId] = useState<number | ''>('');
  const [categories, setCategories] = useState<IVocCategoryWithCount[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const selectModel = new AIIllustrationVocCategorySelectModel();
        const data = await selectModel.get();
        setCategories(data);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
        setError('Failed to load vocabulary categories');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const handleCreate = async () => {
    setError(null);

    // Validation
    if (!name.trim()) {
      setError('Please enter a job name');
      return;
    }

    if (vocCategoryId === '' || vocCategoryId === 0) {
      setError('Please select a vocabulary category');
      return;
    }

    setSaving(true);

    try {
      const data: IAIIllustrationCreate = {
        name: name.trim(),
        voc_category_id: Number(vocCategoryId),
      };

      const response = await model.createJob(data);

      if (response?.data?.id) {
        onSuccess(response.data.id);
      } else {
        setError(response?.data?.message || 'Failed to create job');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while creating the job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Create AI Illustration Job</Title>
        <CloseButton onClick={onClose}>×</CloseButton>
      </Header>

      <Content>
        <InfoBox>
          <InfoTitle>About AI Illustration</InfoTitle>
          <InfoList>
            <li>
              <strong>Purpose:</strong> Enhance vocabulary PNG images using AI to create high-quality alternatives
            </li>
            <li>
              <strong>How it works:</strong> Selects all vocabulary items with PNG uploaded in the category
            </li>
            <li>
              <strong>Results:</strong> Generates enhanced PNG alternatives per item for review
            </li>
          </InfoList>
        </InfoBox>

        <Section>
          <SectionTitle>Job Name *</SectionTitle>
          <NameInput
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Animals Category Enhancement"
            autoFocus
          />
        </Section>

        <Section>
          <SectionTitle>Vocabulary Category *</SectionTitle>
          <Select
            value={vocCategoryId}
            onChange={(e) => {
              const selectedId = Number(e.target.value) || '';
              setVocCategoryId(selectedId);
              // Auto-populate name if empty
              if (!name.trim() && selectedId) {
                const selectedCategory = categories.find((c) => c.id === selectedId);
                if (selectedCategory) {
                  setName(selectedCategory.category);
                }
              }
            }}
            disabled={loadingCategories || saving}
          >
            <option value="">{loadingCategories ? 'Loading categories...' : 'Select a category'}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.category} ({category.voc_count} items)
              </option>
            ))}
          </Select>
          <HelpText>
            Select the vocabulary category to process (only categories with SVG uploaded items are shown)
          </HelpText>
        </Section>

        {error && <ErrorText>{error}</ErrorText>}
      </Content>

      <Footer>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <CreateButton onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating...' : 'Create Job'}
        </CreateButton>
      </Footer>
    </Container>
  );
};

export default AIIllustrationVocabularyCreate;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 30px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const InfoBox = styled.div`
  border-left: 4px solid #2196f3;
  background: #e3f2fd;
  color: #1565c0;
  padding: 12px 14px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const InfoTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 15px;
`;

const InfoList = styled.ul`
  margin: 0;
  padding-left: 20px;
  li {
    line-height: 1.5;
    margin-bottom: 6px;
    font-size: 14px;
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 16px;
  color: #333;
`;

const NameInput = styled.input`
  width: 94%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }
`;

const Select = styled.select`
  width: 97%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 8px;
  font-style: italic;
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 10px;
  padding: 10px;
  background-color: #ffebee;
  border-radius: 4px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const CreateButton = styled.button`
  padding: 10px 20px;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    background-color: #1976d2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
